import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { Theme } from 'ui_system/theme';

// an accessible checkbox that uses a real but hidden checkbox and a
// label that uses styled psuedo-selectors to look like a checkbox
//
// Off state: square shape, grey border, white fill
// On state:  square shape, grey border, white fill, dark checkmark
const accessibleCheckboxLabelStyles = (props) => css`
  cursor: pointer;
  position: relative;
  display: inline-block;
  padding-left: ${`calc(${props.size} + 10px)`}; // space between checkbox and label
  font-size: ${`calc(${props.size} * 2 / 3)`}; // label text
  line-height: ${props.size};
  color: ${Theme.colors.primaryTextColor};
  @media (max-width: 600px) {
    font-size: ${`calc(${props.size} * .6)`}; // label text
  }

  @media (max-width: ${Theme.mobile.phoneWidth - 1}px) {
    font-size: ${`calc(${props.size} * 1 / 2)`};
  }

  &::before,
  &::after {
    display: inline-block;
    position: absolute;
    top: 0px;
    left: 0px;
    line-height: ${props.size}; // line-height of text should match height of checkbox
    height: ${props.size};
    width: ${props.size};
    border: 1px solid ${Theme.colors.borderActionableFieldColor};
  }

  &::before {
    content: '';
  }

  &::after {
    content: 'k';
    text-align: center;
    font-family: 'anyfont';
    font-size: ${`calc(${props.size} * .75)`};
    background-color: ${Theme.colors.backgroundColor};
    color: ${Theme.colors.lightText};
  }

  ${props.modifiers.map((func) => func(props))}
`;

export const AccessibleCheckboxLabel = styled.label`
  ${accessibleCheckboxLabelStyles};
`;

const InvisibleCheckbox = styled.input`
  opacity: 0;
  width: 0;
  height: 0;
  & + ${AccessibleCheckboxLabel}:after {
    content: none;
  }
  &:checked + ${AccessibleCheckboxLabel}:after {
    content: 'k';
  }
`;

// list of modifier functions to change the look of the checkbox
//
// Off state: round shape
// On state:  round shape
export const round = (props) => css`
  &::before {
    border-radius: 50%;
  }
  &::after {
    border-radius: 50%;
    font-size: ${`calc(${props.size} * .50)`};
    border: none;
  }
`;

// Off state: grey border, white fill
// On state:  no border, green fill, white checkmark
export const green = () => css`
  &::before {
    border: 1px solid ${Theme.colors.borderActionableFieldColor};
    background-color: ${Theme.colors.backgroundColor};
  }
  &::after {
    background-color: ${Theme.colors.linksDesignElementsBaseColor};
    color: #fff;
  }
`;

export const greyBeforeClick = () => css`
  &::before {
    background-color: #f2f2f2;
  }
  &::after {
    color: #333;
  }
`;

// Off state: grey border, grey fill
// On state:  no border, green fill, white checkmark
export const greenOnGrey = (props) => css`
  ${green};

  &::before {
    background-color: ${Theme.colors.lightBackground};
  }
  &::after {
    font-size: ${`calc(${props.size} * .45)`};
  }
`;

export const greenMobile = (props) => css`
  ${green};

  &::after {
    font-size: ${`calc(${props.size} * .45)`};
  }
`;

export const checkboxesOnRight = (props) => css`
  padding-left: 0;
  width: 100%;

  &::before,
  &::after {
    float: right;
    position: relative;
  }

  &::after {
    margin-right: ${`calc(${props.size} * -1)`};
  }
`;

export const disabledBoxes = () => css`
  opacity: 0.5;
`;

export const CheckboxAndLabel = styled.div``;

const randomId = (len) =>
  [...Array(len)].map((_) => ((Math.random() * 36) | 0).toString(36)).join('');

export const AccessibleCheckbox = ({
  id,
  children,
  size,
  modifiers,
  ...rest
}) => {
  id = id || randomId(8);
  return (
    <CheckboxAndLabel data-testid="checkbox-select-item">
      <InvisibleCheckbox
        id={id}
        data-testid="radio-btn-input"
        type="checkbox"
        {...rest}
      />
      <AccessibleCheckboxLabel
        id={id}
        htmlFor={id}
        size={size}
        modifiers={modifiers}
        data-testid="offer-at-all-experiences"
      >
        {children}
      </AccessibleCheckboxLabel>
    </CheckboxAndLabel>
  );
};

AccessibleCheckbox.propTypes = {
  id: PropTypes.string.isRequired,
  size: PropTypes.string,
  modifiers: PropTypes.arrayOf(PropTypes.func),
  onChange: PropTypes.func.isRequired,
  children: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
};

AccessibleCheckbox.defaultProps = {
  size: '24px',
  modifiers: [],
  onChange: () => {},
  children: <span />,
};
